import { getBuildMetaSync, loadBuildMeta, loadLatestBuildMeta } from "../version/buildMeta";

const UPDATE_POLL_MS = 60_000;
const BANNER_MIN_VISIBLE_MS = 4_000;

const normalizeBaseUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.endsWith("/") ? raw.slice(0, -1) : raw;
};

const isAbsoluteHttpUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const isSameOrigin = (candidate) => {
  if (!candidate) return false;
  if (typeof window === "undefined") return true;
  try {
    const url = new URL(candidate, window.location.href);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
};

const stripTrailingApiSegment = (baseUrl) => {
  const s = normalizeBaseUrl(baseUrl);
  if (!s) return "";
  if (s.toLowerCase().endsWith("/api")) return s.slice(0, -4);
  return s;
};

// Use environment-provided URLs (CRA build-time env).
// - `REACT_APP_API_URL` is commonly configured as `<backendOrigin>/api`
//   but for UpdateManager we want the backend origin directly (no `/api`),
//   so we strip it and call `/api/meta` explicitly.
const API_BASE_ORIGIN = stripTrailingApiSegment(process.env.REACT_APP_API_URL);
const LEGACY_FRONTEND_BASE_ORIGIN = normalizeBaseUrl(process.env.REACT_APP_API);

const META_ENDPOINT = API_BASE_ORIGIN
  ? `${API_BASE_ORIGIN}/api/meta`
  : "/api/meta";

// User preferences endpoint (existing infrastructure; used only for persisting
// release notes acknowledgement state).
const USER_PREFERENCES_ENDPOINT = (() => {
  const base = normalizeBaseUrl(process.env.REACT_APP_API_URL);
  if (base) return `${base}/user-profile/preferences`;
  return "/api/user-profile/preferences";
})();

const RELEASE_NOTES_URL = (() => {
  const explicit = normalizeBaseUrl(process.env.REACT_APP_RELEASE_NOTES_URL);
  if (explicit) return explicit;

  if (isAbsoluteHttpUrl(LEGACY_FRONTEND_BASE_ORIGIN)) {
    if (isSameOrigin(LEGACY_FRONTEND_BASE_ORIGIN)) {
      return `${LEGACY_FRONTEND_BASE_ORIGIN}/release-notes.json`;
    }
  }

  const publicPath = normalizeBaseUrl(process.env.PUBLIC_URL);
  if (publicPath && !isAbsoluteHttpUrl(publicPath)) return `${publicPath}/release-notes.json`;
  return "/release-notes.json";
})();

// Persisted preferences cache (backend is the source of truth).
// - semantic-version "What's New" acknowledgement cache only
const STORAGE_PREFS_KEY = "app_update_prefs_v1";

// Session-only flags (must not permanently suppress updates):
const SESSION_SUPPRESS_KEY = "app_update_suppress_v1";
const SESSION_REFRESH_PENDING_KEY = "app_update_refresh_pending_v1";

// Legacy keys (cleanup/migration only)
const LEGACY_STORAGE_KEY = "app_update_state_v1";
const LEGACY_STORAGE_REFRESH_PENDING_KEY = "app_update_refresh_pending_v1";

const STORAGE_LAST_HARD_RELOAD_KEY = "app_update_last_hard_reload_v1";
const RELEASE_NOTES_CACHE_KEY_PREFIX = "app_release_notes_cache_v1";

const BROADCAST_CHANNEL_NAME = "app_updates_v1";
const LOCAL_EVENT_KEY = "app_updates_event_v1";

const nowMs = () => Date.now();

const parseJson = (value) => {
  if (!value || typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const safeGetAuthToken = () => {
  try {
    const t1 = localStorage.getItem("access_token");
    if (t1 && String(t1).trim()) return String(t1).trim();
  } catch {}
  try {
    const t2 = sessionStorage.getItem("access_token");
    if (t2 && String(t2).trim()) return String(t2).trim();
  } catch {}
  return "";
};

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const normalizeUpdatePrefs = (value) => {
  if (!isPlainObject(value)) return null;
  const lastSeen =
    typeof value.lastSeenSemanticVersion === "string"
      ? value.lastSeenSemanticVersion.trim()
      : "";
  const dismissed =
    typeof value.dismissedWhatsNewVersion === "string"
      ? value.dismissedWhatsNewVersion.trim()
      : "";

  const next = {};
  if (lastSeen) next.lastSeenSemanticVersion = lastSeen;
  if (dismissed) next.dismissedWhatsNewVersion = dismissed;
  return Object.keys(next).length > 0 ? next : null;
};

const boolFromValue = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  const s = String(value ?? "").trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes";
};

const safeLocalStorageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {}
};

const safeLocalStorageRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {}
};

const safeSessionStorageGet = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionStorageSet = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {}
};

const safeSessionStorageRemove = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {}
};

const safeReload = () => {
  try {
    window.location.reload();
  } catch {
    try {
      window.location.href = window.location.href;
    } catch {}
  }
};

const buildReleaseNotesCacheKey = () => {
  const meta = getBuildMetaSync();
  const buildId = typeof meta?.buildId === "string" ? meta.buildId.trim() : "";
  const version = typeof meta?.version === "string" ? meta.version.trim() : "";

  // Prefer buildId (deployment-aware). Fall back to version (semantic-aware).
  const tag =
    (buildId && buildId !== "unknown" ? buildId : "") ||
    (version && version !== "0.0.0" ? version : "");

  return tag ? `${RELEASE_NOTES_CACHE_KEY_PREFIX}:${tag}` : RELEASE_NOTES_CACHE_KEY_PREFIX;
};

const buildReleaseNotesCacheBuster = () => {
  const meta = getBuildMetaSync();
  const buildId = typeof meta?.buildId === "string" ? meta.buildId.trim() : "";
  const version = typeof meta?.version === "string" ? meta.version.trim() : "";
  return (buildId && buildId !== "unknown" ? buildId : "") || (version && version !== "0.0.0" ? version : "") || String(nowMs());
};

const parseBuildIdSortKey = (buildId) => {
  const s = String(buildId || "").trim();
  const m = /^(\d{8})-(\d{4})-/.exec(s);
  if (!m) return null;
  const key = Number(`${m[1]}${m[2]}`);
  return Number.isFinite(key) ? key : null;
};

const compareBuildIds = (a, b) => {
  const ak = parseBuildIdSortKey(a);
  const bk = parseBuildIdSortKey(b);
  if (ak != null && bk != null) {
    if (ak < bk) return -1;
    if (ak > bk) return 1;
    return 0;
  }
  const as = String(a || "");
  const bs = String(b || "");
  if (as < bs) return -1;
  if (as > bs) return 1;
  return 0;
};

const semverToTupleValue = (version) => {
  const s = String(version || "").trim();
  const m = /^(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/.exec(s);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
};

const compareSemverValues = (a, b) => {
  const at = semverToTupleValue(a);
  const bt = semverToTupleValue(b);
  if (!at || !bt) {
    const as = String(a || "");
    const bs = String(b || "");
    if (as < bs) return -1;
    if (as > bs) return 1;
    return 0;
  }
  for (let i = 0; i < 3; i += 1) {
    if (at[i] < bt[i]) return -1;
    if (at[i] > bt[i]) return 1;
  }
  return 0;
};

const normalizeBuildId = (value) => {
  const s = String(value ?? "").trim();
  if (!s) return "";
  if (s.toLowerCase() === "unknown") return "";
  return s;
};

const normalizeVersion = (value) => {
  const s = String(value ?? "").trim();
  if (!s) return "";
  if (s === "0.0.0") return "";
  return s;
};

const getFrontendPolicy = (meta) => {
  const nested =
    meta?.frontendPolicy && typeof meta.frontendPolicy === "object"
      ? meta.frontendPolicy
      : {};

  const latestBuildId = normalizeBuildId(
    nested.latestBuildId ?? meta?.frontendLatestBuildId,
  );
  const latestVersion = normalizeVersion(
    nested.latestVersion ?? meta?.frontendLatestVersion,
  );
  const minimumSupportedBuildId = normalizeBuildId(
    nested.minimumSupportedBuildId ?? meta?.frontendMinBuildId,
  );
  const minimumSupportedVersion = normalizeVersion(
    nested.minimumSupportedVersion ?? meta?.frontendMinVersion,
  );
  const forceUpdate = boolFromValue(nested.forceUpdate ?? meta?.frontendForceUpdate);

  const isAvailable = Boolean(
    latestBuildId ||
      latestVersion ||
      minimumSupportedBuildId ||
      minimumSupportedVersion ||
      forceUpdate,
  );

  return {
    latestBuildId: latestBuildId || null,
    latestVersion: latestVersion || null,
    minimumSupportedBuildId: minimumSupportedBuildId || null,
    minimumSupportedVersion: minimumSupportedVersion || null,
    forceUpdate,
    isAvailable,
  };
};

export const evaluateFrontendUpdatePolicy = ({ frontend, backendMeta, latestFrontendMeta = null }) => {
  const frontendPolicy = getFrontendPolicy(backendMeta || {});
  const frontendBuildId = normalizeBuildId(frontend?.buildId);
  const frontendVersion = normalizeVersion(frontend?.version);
  const latestBuildId =
    normalizeBuildId(frontendPolicy.latestBuildId) ||
    normalizeBuildId(latestFrontendMeta?.buildId);
  const latestVersion =
    normalizeVersion(frontendPolicy.latestVersion) ||
    normalizeVersion(latestFrontendMeta?.version);
  const minimumSupportedBuildId = normalizeBuildId(frontendPolicy.minimumSupportedBuildId);
  const minimumSupportedVersion = normalizeVersion(frontendPolicy.minimumSupportedVersion);
  const effectiveFrontendPolicy = {
    ...frontendPolicy,
    latestBuildId: latestBuildId || null,
    latestVersion: latestVersion || null,
    isAvailable: Boolean(
      frontendPolicy.isAvailable ||
        latestBuildId ||
        latestVersion,
    ),
  };
  const canCompareMinBuildIds = Boolean(
    minimumSupportedBuildId &&
      frontendBuildId &&
      parseBuildIdSortKey(minimumSupportedBuildId) != null &&
      parseBuildIdSortKey(frontendBuildId) != null,
  );

  const belowMinBuild =
    canCompareMinBuildIds && compareBuildIds(frontendBuildId, minimumSupportedBuildId) < 0;
  const belowMinVersion =
    Boolean(minimumSupportedVersion && frontendVersion) &&
    compareSemverValues(frontendVersion, minimumSupportedVersion) < 0;
  const latestBuildAvailable =
    Boolean(latestBuildId && frontendBuildId) && latestBuildId !== frontendBuildId;
  const latestVersionAvailable =
    !latestBuildAvailable &&
    Boolean(latestVersion && frontendVersion) &&
    compareSemverValues(latestVersion, frontendVersion) > 0;

  if (effectiveFrontendPolicy.forceUpdate) {
    return {
      phase: "FORCE_UPDATE",
      status: "force_update",
      reason: "frontend_force_update",
      message: "Update required. Reload the application to continue.",
      updateKey:
        latestBuildId ||
        latestVersion ||
        minimumSupportedBuildId ||
        minimumSupportedVersion ||
        frontendBuildId ||
        frontendVersion ||
        null,
      frontendPolicy: effectiveFrontendPolicy,
      diagnostics: {
        belowMinBuild,
        belowMinVersion,
        latestBuildAvailable,
        latestVersionAvailable,
      },
    };
  }

  if (belowMinVersion || belowMinBuild) {
    return {
      phase: "FORCE_UPDATE",
      status: "force_update",
      reason: "frontend_below_minimum",
      message: "Update required. Reload the application to continue.",
      updateKey:
        minimumSupportedBuildId ||
        minimumSupportedVersion ||
        latestBuildId ||
        latestVersion ||
        null,
      frontendPolicy: effectiveFrontendPolicy,
      diagnostics: {
        belowMinBuild,
        belowMinVersion,
        latestBuildAvailable,
        latestVersionAvailable,
      },
    };
  }

  if (latestBuildAvailable || latestVersionAvailable) {
    return {
      phase: "UPDATE_AVAILABLE",
      status: "update_available",
      reason: latestBuildAvailable ? "frontend_build_available" : "frontend_version_available",
      message: "New version available",
      updateKey: latestBuildId || latestVersion || null,
      frontendPolicy: effectiveFrontendPolicy,
      diagnostics: {
        belowMinBuild,
        belowMinVersion,
        latestBuildAvailable,
        latestVersionAvailable,
      },
    };
  }

  return {
    phase: "IDLE",
    status: "up_to_date",
    reason: null,
    message: null,
    updateKey: null,
    frontendPolicy: effectiveFrontendPolicy,
    diagnostics: {
      belowMinBuild,
      belowMinVersion,
      latestBuildAvailable,
      latestVersionAvailable,
      policyUnavailable: !effectiveFrontendPolicy.isAvailable,
    },
  };
};

const isStreamingActive = () => {
  // ChatPage persists active stream state in localStorage under:
  //   `chat_active_stream:<session_id>`
  try {
    const ttlMs = 30 * 60 * 1000;
    const prefix = "chat_active_stream:";
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const raw = localStorage.getItem(key);
      const parsed = raw ? parseJson(raw) : null;
      if (!parsed || typeof parsed !== "object") continue;

      const createdAtMs = Number(parsed.createdAtMs || 0) || 0;
      if (createdAtMs > 0 && nowMs() - createdAtMs > ttlMs) {
        safeLocalStorageRemove(key);
        continue;
      }

      const updatedAtMs = Number(parsed.updatedAtMs || 0) || createdAtMs;
      if (updatedAtMs > 0 && nowMs() - updatedAtMs < ttlMs) return true;
      if (!updatedAtMs) return true;
    }
  } catch {}
  return false;
};

const shouldThrottleHardReload = () => {
  const lastRaw = safeSessionStorageGet(STORAGE_LAST_HARD_RELOAD_KEY);
  const last = Number(lastRaw || 0) || 0;
  return last > 0 && nowMs() - last < 30_000;
};

const markHardReload = () => {
  safeSessionStorageSet(STORAGE_LAST_HARD_RELOAD_KEY, String(nowMs()));
};

const defaultState = () => ({
  isInitialized: false,
  // Explicit state machine (enterprise-grade / deduped UI lifecycle)
  phase: "IDLE", // IDLE|UPDATE_AVAILABLE|FORCE_UPDATE|UPDATING|DISMISSED
  status: "up_to_date", // backwards-compatible: up_to_date|update_available|force_update
  message: null,
  detectedAt: null,
  refreshPending: false,
  frontend: getBuildMetaSync(),
  backend: null,
  frontendPolicy: null,
  reason: null, // frontend_build_available|frontend_version_available|frontend_below_minimum|frontend_force_update|chunk_error
  updateKey: null, // frontend target build/version for UPDATE_AVAILABLE/FORCE_UPDATE
  suppressedUpdateKey: null, // session-only suppression key for frontend update target
  // "What's New" / release notes state
  releaseNotes: null, // array (newest first)
  releaseNotesLoadedAt: null,
  whatsNew: null, // { mode, version, entry } where mode=whats_new|update_available
  lastSeenSemanticVersion: null,
  dismissedWhatsNewVersion: null,
});

class UpdateManagerImpl {
  constructor() {
    this.started = false;
    this.startPromise = null;
    this.intervalId = null;
    this.listeners = new Set();
    this.bc = null;
    this.lastNotifiedUiKey = null;
    this.popupMinVisibleUntilMs = 0;
    this.reloadRequestedAtMs = 0;
    this.reloadInFlight = false;
    this.state = this.loadState();
    this.releaseNotesInMemory = null;
    this.releaseNotesLoadedAtMs = 0;
    this.releaseNotesInFlight = null;
    this.hasServerPrefs = false;
    this.didAttemptLegacyMigration = false;

    this.onStorage = this.onStorage.bind(this);
    this.onBroadcast = this.onBroadcast.bind(this);

    this.lastNotifiedUiKey = this.getUiKey(this.state);
  }

  isDebugEnabled() {
    // Prefer runtime toggle via localStorage for on-demand debugging (no rebuild required).
    // Note: index.js disables console.log/warn/error in prod, so we use console.info.
    return (
      boolFromValue(process.env.REACT_APP_UPDATE_DEBUG) ||
      safeLocalStorageGet("app_update_debug") === "1"
    );
  }

  debug(event, details) {
    if (!this.isDebugEnabled()) return;
    try {
      const payload = details && typeof details === "object" ? details : undefined;
      if (payload) console.info("[UpdateManager]", event, payload);
      else console.info("[UpdateManager]", event);
    } catch {}
  }

  loadPrefs() {
    const raw = safeLocalStorageGet(STORAGE_PREFS_KEY);
    const parsed = raw ? parseJson(raw) : null;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  }

  persistPrefs(nextPrefs) {
    try {
      safeLocalStorageSet(STORAGE_PREFS_KEY, JSON.stringify(nextPrefs));
    } catch {}
  }

  isAckDecisionReady(state) {
    const token = safeGetAuthToken();
    if (!token) return true; // unauthenticated: cache-driven like today
    if (this.hasServerPrefs) return true;
    const s = state || this.state;
    const hasCache =
      Boolean(String(s?.lastSeenSemanticVersion || "").trim()) ||
      Boolean(String(s?.dismissedWhatsNewVersion || "").trim());
    return hasCache;
  }

  async persistUpdatePrefsToBackend(updates) {
    const normalized = normalizeUpdatePrefs(updates);
    const token = safeGetAuthToken();
    if (!normalized || !token) return { ok: false, skipped: true };

    try {
      const resp = await fetch(USER_PREFERENCES_ENDPOINT, {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        cache: "no-store",
        body: JSON.stringify({ preferences: { updates: normalized } }),
      });
      if (!resp.ok) throw new Error(`prefs ${resp.status}`);
      return { ok: true };
    } catch {
      return { ok: false };
    }
  }

  computeWhatsNewForState({ state, releaseNotes }) {
    const s = state || this.state;
    const notes = Array.isArray(releaseNotes)
      ? releaseNotes
      : Array.isArray(s?.releaseNotes)
        ? s.releaseNotes
        : this.releaseNotesInMemory;

    const currentVersion = String(
      s?.frontend?.version || getBuildMetaSync()?.version || "",
    ).trim();
    if (!notes || notes.length === 0 || !currentVersion) return null;

    const dismissed = String(s?.dismissedWhatsNewVersion || "").trim();
    const lastSeen = String(s?.lastSeenSemanticVersion || "").trim();

    // Standalone "What's New" (IDLE) — show once per semantic version.
    if (String(s?.phase || "") === "IDLE") {
      if ((!lastSeen || lastSeen !== currentVersion) && dismissed !== currentVersion) {
        const computed = this.computeWhatsNew({
          frontendVersion: currentVersion,
          updateSemanticVersion: null,
          releaseNotes: notes,
        });
        return computed && computed.mode === "whats_new" ? computed : null;
      }
      return null;
    }

    // Update banner "What's New" — only when a newer semantic version exists.
    const phase = String(s?.phase || "");
    const status = String(s?.status || "");
    const isUpdate =
      status === "update_available" ||
      status === "force_update" ||
      phase === "UPDATE_AVAILABLE" ||
      phase === "FORCE_UPDATE" ||
      phase === "UPDATING";

    if (!isUpdate) return null;

    const latest = notes[0]?.version ? String(notes[0].version).trim() : "";
    const updateSemanticVersion =
      latest && this.compareSemver(latest, currentVersion) > 0 ? latest : null;

    if (!updateSemanticVersion) return null;

    const computed = this.computeWhatsNew({
      frontendVersion: currentVersion,
      updateSemanticVersion,
      releaseNotes: notes,
    });

    if (dismissed && computed?.version && dismissed === computed.version) return null;
    return computed || null;
  }

  applyServerUpdatePrefs(value, { broadcast = true } = {}) {
    const normalized = normalizeUpdatePrefs(value);
    if (!normalized) return;

    this.hasServerPrefs = true;

    const nextPrefs = {
      lastSeenSemanticVersion:
        typeof normalized.lastSeenSemanticVersion === "string"
          ? normalized.lastSeenSemanticVersion
          : null,
      dismissedWhatsNewVersion:
        typeof normalized.dismissedWhatsNewVersion === "string"
          ? normalized.dismissedWhatsNewVersion
          : null,
    };

    // Backend always wins; update cache and recompute UI state if needed.
    this.persistPrefs(nextPrefs);

    const prev = this.state;
    const next = {
      ...prev,
      lastSeenSemanticVersion: nextPrefs.lastSeenSemanticVersion || null,
      dismissedWhatsNewVersion: nextPrefs.dismissedWhatsNewVersion || null,
    };

    const whatsNew = this.computeWhatsNewForState({ state: { ...next }, releaseNotes: prev.releaseNotes });

    if (broadcast) this.broadcast({ type: "prefs_updates", updates: normalized });
    this.commitState({ ...next, whatsNew }, { broadcast, allowImmediateHide: true });
  }

  onUserPreferencesResponse(payload) {
    // Mark that we've consulted backend state for this user/session.
    this.hasServerPrefs = true;

    const updates = payload?.preferences?.updates;
    const normalized = normalizeUpdatePrefs(updates);
    if (normalized) {
      this.applyServerUpdatePrefs(normalized, { broadcast: true });
      return;
    }

    // Back-compat: if server has no state yet, migrate cache once (best-effort).
    const cached = normalizeUpdatePrefs(this.loadPrefs());
    if (!cached) {
      // Server has no acknowledgement yet (new user / fresh DB state). Treat as unseen and
      // recompute UI "What's New" immediately now that backend has been consulted.
      const whatsNew = this.computeWhatsNewForState({ state: this.state, releaseNotes: this.state.releaseNotes });
      this.commitState({ ...this.state, whatsNew }, { broadcast: false, allowImmediateHide: true });
      return;
    }

    const token = safeGetAuthToken();
    if (!token) return;

    // Avoid repeated writes; backend remains the source of truth once it persists.
    if (this.didAttemptLegacyMigration) return;
    this.didAttemptLegacyMigration = true;
    this.persistUpdatePrefsToBackend(cached).catch(() => {});
  }

  loadSuppression() {
    const raw = safeSessionStorageGet(SESSION_SUPPRESS_KEY);
    const parsed = raw ? parseJson(raw) : null;
    if (!parsed || typeof parsed !== "object") return null;
    const updateKey = normalizeBuildId(parsed.updateKey);
    if (!updateKey) return null;
    return { updateKey, suppressedAtMs: Number(parsed.suppressedAtMs || 0) || 0 };
  }

  suppressUpdateForSession(updateKey) {
    const key = normalizeBuildId(updateKey);
    if (!key) return;
    safeSessionStorageSet(
      SESSION_SUPPRESS_KEY,
      JSON.stringify({ updateKey: key, suppressedAtMs: nowMs() }),
    );
  }

  clearSuppression() {
    safeSessionStorageRemove(SESSION_SUPPRESS_KEY);
  }

  getRefreshPendingFlag() {
    return safeSessionStorageGet(SESSION_REFRESH_PENDING_KEY) === "1";
  }

  setRefreshPendingFlag(value) {
    safeSessionStorageSet(SESSION_REFRESH_PENDING_KEY, value ? "1" : "0");
  }

  loadState() {
    const base = defaultState();

    // ------------------------------------------------------------------
    // Legacy migration/cleanup (do NOT restore update popup state).
    // ------------------------------------------------------------------
    try {
      const legacyRaw = safeLocalStorageGet(LEGACY_STORAGE_KEY);
      const legacy = legacyRaw ? parseJson(legacyRaw) : null;
      if (legacy && typeof legacy === "object") {
        const prefs = this.loadPrefs();
        const nextPrefs = {
          ...prefs,
          lastSeenSemanticVersion:
            typeof legacy.lastSeenSemanticVersion === "string"
              ? legacy.lastSeenSemanticVersion
              : prefs.lastSeenSemanticVersion,
          dismissedWhatsNewVersion:
            typeof legacy.dismissedWhatsNewVersion === "string"
              ? legacy.dismissedWhatsNewVersion
              : prefs.dismissedWhatsNewVersion,
        };
        this.persistPrefs(nextPrefs);
      }
    } catch {}

    safeLocalStorageRemove(LEGACY_STORAGE_KEY);
    safeLocalStorageRemove(LEGACY_STORAGE_REFRESH_PENDING_KEY);

    const prefs = this.loadPrefs();
    const suppression = this.loadSuppression();

    return {
      ...base,
      isInitialized: false,
      frontend: base.frontend,
      refreshPending: this.getRefreshPendingFlag(),
      suppressedUpdateKey: suppression?.updateKey || null,
      lastSeenSemanticVersion:
        typeof prefs.lastSeenSemanticVersion === "string"
          ? prefs.lastSeenSemanticVersion
          : null,
      dismissedWhatsNewVersion:
        typeof prefs.dismissedWhatsNewVersion === "string"
          ? prefs.dismissedWhatsNewVersion
          : null,
    };
  }

  getBackendBuildId(state) {
    const raw =
      typeof state?.backend?.backendBuildId === "string"
        ? state.backend.backendBuildId
        : "";
    const s = raw.trim();
    return s || "";
  }

  getFrontendBuildId(state) {
    const raw = typeof state?.frontend?.buildId === "string" ? state.frontend.buildId : "";
    const s = String(raw || "").trim();
    return s || "";
  }

  getWhatsNewKey(whatsNew) {
    if (!whatsNew || typeof whatsNew !== "object") return "";
    const mode = typeof whatsNew.mode === "string" ? whatsNew.mode : "";
    const version = typeof whatsNew.version === "string" ? whatsNew.version : "";
    const entry = whatsNew.entry && typeof whatsNew.entry === "object" ? whatsNew.entry : null;
    if (!mode || !version) return "";
    const title = typeof entry?.title === "string" ? entry.title : "";
    const desc = typeof entry?.description === "string" ? entry.description : "";
    const features = Array.isArray(entry?.features) ? entry.features.slice(0, 8) : [];
    return JSON.stringify({ mode, version, title, desc, features });
  }

  getReleaseNotesKey(list) {
    if (!Array.isArray(list) || list.length === 0) return "";
    const versions = list
      .slice(0, 6)
      .map((x) => (x && typeof x.version === "string" ? x.version : ""))
      .filter(Boolean);
    return versions.join("|");
  }

  getUiKey(state) {
    const s = state || {};
    return JSON.stringify({
      isInitialized: Boolean(s.isInitialized),
      phase: String(s.phase || ""),
      status: String(s.status || ""),
      refreshPending: Boolean(s.refreshPending),
      frontendBuildId: this.getFrontendBuildId(s),
      backendBuildId: this.getBackendBuildId(s),
      frontendPolicy: JSON.stringify(s.frontendPolicy || {}),
      updateKey: String(s.updateKey || ""),
      suppressedUpdateKey: String(s.suppressedUpdateKey || ""),
      whatsNew: this.getWhatsNewKey(s.whatsNew),
      releaseNotes: this.getReleaseNotesKey(s.releaseNotes),
    });
  }

  isPopupPhase(phase) {
    return phase === "UPDATE_AVAILABLE" || phase === "FORCE_UPDATE" || phase === "UPDATING";
  }

  applyPhaseStability(prev, next, { allowImmediateHide = false } = {}) {
    const t = nowMs();
    const prevPhase = String(prev?.phase || "IDLE");
    const nextPhase = String(next?.phase || "IDLE");
    const prevVisible = this.isPopupPhase(prevPhase);
    const desiredVisible = this.isPopupPhase(nextPhase);

    if (desiredVisible && !prevVisible) {
      this.popupMinVisibleUntilMs = t + BANNER_MIN_VISIBLE_MS;
    }

    const shouldHold =
      !desiredVisible && prevVisible && !allowImmediateHide && t < this.popupMinVisibleUntilMs;

    if (!shouldHold) return next;

    // Hold the previous popup phase briefly to avoid flicker from transient backend responses.
    return { ...next, phase: prevPhase, status: prev.status, reason: prev.reason, message: prev.message, detectedAt: prev.detectedAt, updateKey: prev.updateKey };
  }

  commitState(next, { broadcast = false, allowImmediateHide = false } = {}) {
    const prev = this.state;
    const stable = this.applyPhaseStability(prev, next, { allowImmediateHide });
    this.state = stable;

    if (String(prev?.phase || "") !== String(stable?.phase || "")) {
      this.debug("transition", {
        from: String(prev?.phase || ""),
        to: String(stable?.phase || ""),
        reason: String(stable?.reason || ""),
        frontendBuildId: normalizeBuildId(stable?.frontend?.buildId),
        backendBuildId: normalizeBuildId(stable?.backend?.backendBuildId),
        updateKey: String(stable?.updateKey || ""),
        suppressedUpdateKey: String(stable?.suppressedUpdateKey || ""),
      });
    }

    if (broadcast) this.broadcast({ type: "state", state: stable });

    const uiKey = this.getUiKey(stable);
    if (uiKey === this.lastNotifiedUiKey) return;
    this.lastNotifiedUiKey = uiKey;
    this.notify();
  }

  setWhatsNewSeen(version) {
    const v = String(version || "").trim();
    if (!v) return;
    const next = {
      ...this.state,
      lastSeenSemanticVersion: v,
      dismissedWhatsNewVersion: v,
      whatsNew: null,
    };
    this.persistPrefs({ lastSeenSemanticVersion: v, dismissedWhatsNewVersion: v });
    this.broadcast({ type: "prefs_updates", updates: { lastSeenSemanticVersion: v, dismissedWhatsNewVersion: v } });
    this.persistUpdatePrefsToBackend({ lastSeenSemanticVersion: v, dismissedWhatsNewVersion: v }).catch(() => {});
    this.commitState(next, { broadcast: true, allowImmediateHide: true });
  }

  dismissWhatsNew(version) {
    const v = String(version || "").trim();
    if (!v) return;
    const next = {
      ...this.state,
      dismissedWhatsNewVersion: v,
      lastSeenSemanticVersion: v,
      whatsNew: null,
    };
    this.persistPrefs({ lastSeenSemanticVersion: v, dismissedWhatsNewVersion: v });
    this.broadcast({ type: "prefs_updates", updates: { lastSeenSemanticVersion: v, dismissedWhatsNewVersion: v } });
    this.persistUpdatePrefsToBackend({ lastSeenSemanticVersion: v, dismissedWhatsNewVersion: v }).catch(() => {});
    this.commitState(next, { broadcast: true, allowImmediateHide: true });
  }

  semverToTuple(version) {
    return semverToTupleValue(version);
  }

  compareSemver(a, b) {
    return compareSemverValues(a, b);
  }

  normalizeReleaseNotes(list) {
    if (!Array.isArray(list)) return null;
    const normalized = [];
    for (const item of list) {
      if (!item || typeof item !== "object") continue;
      const version =
        typeof item.version === "string" ? item.version.trim() : "";
      if (!version) continue;
      normalized.push({
        version,
        title: typeof item.title === "string" ? item.title : "",
        description: typeof item.description === "string" ? item.description : "",
        features: Array.isArray(item.features)
          ? item.features.filter((f) => typeof f === "string" && f.trim()).map((f) => f.trim())
          : [],
        releasedAt: typeof item.releasedAt === "string" ? item.releasedAt : null,
      });
    }
    // Ensure newest first (best-effort sort by semver desc)
    normalized.sort((a, b) => this.compareSemver(b.version, a.version));
    return normalized;
  }

  loadReleaseNotesFromStorage() {
    const raw = safeLocalStorageGet(buildReleaseNotesCacheKey());
    const parsed = raw ? parseJson(raw) : null;
    if (!parsed || typeof parsed !== "object") return null;
    const list = this.normalizeReleaseNotes(parsed.items);
    if (!list) return null;
    const loadedAtMs = Number(parsed.loadedAtMs || 0) || 0;
    return { list, loadedAtMs };
  }

  persistReleaseNotesToStorage(list) {
    try {
      safeLocalStorageSet(
        buildReleaseNotesCacheKey(),
        JSON.stringify({ loadedAtMs: nowMs(), items: list }),
      );
    } catch {}
  }

  async ensureReleaseNotesLoaded({ force = false } = {}) {
    if (this.releaseNotesInFlight) return this.releaseNotesInFlight;

    const ttlMs = 10 * 60 * 1000;
    const isFresh = this.releaseNotesInMemory && nowMs() - this.releaseNotesLoadedAtMs < ttlMs;
    if (!force && isFresh) return this.releaseNotesInMemory;

    // Try storage cache first (multi-tab friendly)
    if (!force) {
      const cached = this.loadReleaseNotesFromStorage();
      if (cached && cached.list && cached.loadedAtMs && nowMs() - cached.loadedAtMs < ttlMs) {
        this.releaseNotesInMemory = cached.list;
        this.releaseNotesLoadedAtMs = cached.loadedAtMs;
        return cached.list;
      }
    }

    this.releaseNotesInFlight = (async () => {
      try {
        const cacheBuster = buildReleaseNotesCacheBuster();
        const join = RELEASE_NOTES_URL.includes("?") ? "&" : "?";
        const url = `${RELEASE_NOTES_URL}${join}v=${encodeURIComponent(cacheBuster)}`;

        const resp = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
          cache: "no-store",
        });
        if (!resp.ok) throw new Error(`release-notes ${resp.status}`);
        const json = await resp.json();
        const list = this.normalizeReleaseNotes(json);
        if (!list) throw new Error("release-notes invalid");

        this.releaseNotesInMemory = list;
        this.releaseNotesLoadedAtMs = nowMs();
        this.persistReleaseNotesToStorage(list);

        return list;
      } catch {
        return this.releaseNotesInMemory;
      } finally {
        this.releaseNotesInFlight = null;
      }
    })();

    return this.releaseNotesInFlight;
  }

  computeWhatsNew({ frontendVersion, updateSemanticVersion, releaseNotes }) {
    const current = String(frontendVersion || "").trim();
    const update = String(updateSemanticVersion || "").trim();
    const list = Array.isArray(releaseNotes) ? releaseNotes : null;
    if (!list || list.length === 0) return null;

    const findEntry = (v) => list.find((x) => x.version === v) || null;

    // Case A: update available and semantic version changed (show notes for the update target)
    if (update && update !== current) {
      const entry = findEntry(update) || findEntry(list[0]?.version);
      if (!entry) return null;
      return { mode: "update_available", version: entry.version, entry };
    }

    // Case B: already on latest (show notes once per semantic version on first run)
    const entry = findEntry(current) || null;
    if (!entry) return null;
    return { mode: "whats_new", version: entry.version, entry };
  }

  persistState(next) {
    // Backwards-compatible helper (no-op persistence).
    this.state = next;
  }

  notify() {
    for (const fn of this.listeners) {
      try {
        fn(this.state);
      } catch {}
    }
  }

  broadcast(payload) {
    try {
      if (this.bc) this.bc.postMessage(payload);
    } catch {}
    try {
      // Fallback cross-tab event channel when BroadcastChannel is unavailable.
      safeLocalStorageSet(LOCAL_EVENT_KEY, JSON.stringify({ ...payload, t: nowMs() }));
    } catch {}
  }

  onBroadcast(event) {
    const data = event?.data;
    if (!data || typeof data !== "object") return;
    if (data.type === "state") {
      // Enterprise-grade requirement: state is driven from backend + build meta.
      // We only accept user-action hints cross-tab; poll will reconcile.
      const updateKey = normalizeBuildId(data?.state?.updateKey);
      const phase = String(data?.state?.phase || "");
      if (phase === "DISMISSED" && updateKey) {
        this.suppressUpdateForSession(updateKey);
        this.commitState({ ...this.state, phase: "DISMISSED", status: "up_to_date", updateKey, suppressedUpdateKey: updateKey }, { broadcast: false, allowImmediateHide: true });
      }
      return;
    }
    if (data.type === "refresh_pending") this.setRefreshPending(true, { silent: true });
    if (data.type === "prefs_updates" && data.updates) {
      this.applyServerUpdatePrefs(data.updates, { broadcast: false });
    }
  }

  onStorage(e) {
    const key = e?.key;
    if (!key) return;
    if (key === LOCAL_EVENT_KEY && e.newValue) {
      const payload = parseJson(e.newValue);
      const t = Number(payload?.t || 0) || 0;
      if (t > 0 && nowMs() - t > 10_000) return; // ignore stale events
      if (payload?.type === "refresh_pending") this.setRefreshPending(true, { silent: true });
      if (payload?.type === "prefs_updates" && payload?.updates) {
        this.applyServerUpdatePrefs(payload.updates, { broadcast: false });
      }
    }
  }

  subscribe(fn) {
    this.listeners.add(fn);
    try {
      fn(this.state);
    } catch {}
    return () => this.listeners.delete(fn);
  }

  async start() {
    if (this.started) return this.startPromise || undefined;
    this.started = true;

    this.startPromise = (async () => {
      // Multi-tab sync first to avoid missing remote events during init.
      try {
        if ("BroadcastChannel" in window) {
          this.bc = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
          this.bc.addEventListener("message", this.onBroadcast);
        }
      } catch {
        this.bc = null;
      }

      try {
        window.addEventListener("storage", this.onStorage);
      } catch {}

      // Startup stabilization: meta -> notes -> first poll -> mark initialized.
      let working = { ...this.state, isInitialized: false };

      const meta = await loadBuildMeta().catch(() => null);
      if (meta) working = { ...working, frontend: meta };

      const notes = await this.ensureReleaseNotesLoaded().catch(() => null);
      if (Array.isArray(notes) && notes.length > 0) {
        working = {
          ...working,
          releaseNotes: notes,
          releaseNotesLoadedAt: new Date().toISOString(),
        };

        const currentVersion = String(meta?.version || working.frontend?.version || "").trim();
        if (currentVersion && this.isAckDecisionReady(working)) {
          const lastSeen = String(working.lastSeenSemanticVersion || "").trim();
          const dismissed = String(working.dismissedWhatsNewVersion || "").trim();
          if ((!lastSeen || lastSeen !== currentVersion) && dismissed !== currentVersion) {
            const whatsNew = this.computeWhatsNew({
              frontendVersion: currentVersion,
              updateSemanticVersion: null,
              releaseNotes: notes,
            });
            if (whatsNew && whatsNew.mode === "whats_new") {
              working = { ...working, whatsNew };
            }
          }
        }
      }

      working =
        (await this.pollOnce({ prevState: working, commit: false }).catch(() => working)) ||
        working;

      working = { ...working, isInitialized: true };

      // Single startup emit.
      this.commitState(working, { broadcast: true });

      this.intervalId = setInterval(() => {
        this.pollOnce().catch(() => {});
        this.maybeAutoRefresh().catch(() => {});
      }, UPDATE_POLL_MS);
    })();

    return this.startPromise;
  }

  stop() {
    if (!this.started) return;
    this.started = false;
    this.startPromise = null;
    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = null;
    try {
      window.removeEventListener("storage", this.onStorage);
    } catch {}
    try {
      if (this.bc) {
        this.bc.removeEventListener("message", this.onBroadcast);
        this.bc.close();
      }
    } catch {}
    this.bc = null;
  }

  dismiss() {
    // "Restart later" (session-only suppression). Must not permanently suppress future updates.
    const updateKey = normalizeBuildId(this.state?.updateKey) || normalizeVersion(this.state?.updateKey);
    if (!updateKey) {
      this.debug("dismiss:no-update-key", {});
      const next = { ...this.state, phase: "DISMISSED", status: "up_to_date", updateKey: null };
      this.commitState(next, { broadcast: true, allowImmediateHide: true });
      return;
    }

    this.suppressUpdateForSession(updateKey);
    const next = {
      ...this.state,
      phase: "DISMISSED",
      status: "up_to_date",
      updateKey,
      suppressedUpdateKey: updateKey,
      refreshPending: false,
    };
    this.setRefreshPendingFlag(false);
    this.debug("dismiss:suppressed", { updateKey });
    this.commitState(next, { broadcast: true, allowImmediateHide: true });
  }

  requestRefresh() {
    if (this.reloadInFlight) {
      this.debug("refresh:deduped", {});
      return { deferred: false, deduped: true };
    }

    if (isStreamingActive()) {
      this.setRefreshPending(true);
      this.debug("refresh:deferred_streaming", {});
      return { deferred: true };
    }

    // Avoid refresh loops on next boot/session.
    this.setRefreshPending(false, { silent: true });

    // Dedup rapid repeated triggers from multiple callers.
    const t = nowMs();
    if (this.reloadRequestedAtMs > 0 && t - this.reloadRequestedAtMs < 10_000) {
      this.debug("refresh:deduped_window", { dtMs: t - this.reloadRequestedAtMs });
      return { deferred: false, deduped: true };
    }
    this.reloadRequestedAtMs = t;
    this.reloadInFlight = true;

    const kind =
      this.state?.phase === "FORCE_UPDATE" || this.state?.status === "force_update"
        ? "FORCE_UPDATE"
        : "UPDATE_AVAILABLE";

    this.debug("refresh:trigger", {
      kind,
      frontendBuildId: normalizeBuildId(this.state?.frontend?.buildId),
      backendBuildId: normalizeBuildId(this.state?.backend?.backendBuildId),
    });

    const next = {
      ...this.state,
      phase: "UPDATING",
      status: kind === "FORCE_UPDATE" ? "force_update" : "update_available",
    };
    this.commitState(next, { broadcast: true, allowImmediateHide: true });

    markHardReload();
    safeReload();
    return { deferred: false };
  }

  setRefreshPending(value, { silent = false } = {}) {
    this.setRefreshPendingFlag(Boolean(value));
    const next = { ...this.state, refreshPending: Boolean(value) };
    if (!silent) this.broadcast({ type: "refresh_pending" });
    this.debug("refresh_pending:set", { value: Boolean(value), silent: Boolean(silent) });
    this.commitState(next, { broadcast: false, allowImmediateHide: true });
  }

  async maybeAutoRefresh() {
    const pending = this.getRefreshPendingFlag();
    if (!pending) return;
    if (isStreamingActive()) return;
    if (shouldThrottleHardReload()) return;
    this.debug("refresh:auto", {});
    this.requestRefresh();
  }

  async pollOnce({ prevState = null, commit = true } = {}) {
    const prev = prevState || this.state;
    let backendMeta = null;
    try {
      const resp = await fetch(META_ENDPOINT, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
        credentials: "include",
      });
      if (!resp.ok) return prev;
      backendMeta = await resp.json();
    } catch {
      return prev;
    }
    if (!backendMeta || typeof backendMeta !== "object") return prev;

    const frontend = getBuildMetaSync();
    const latestFrontendMeta = await loadLatestBuildMeta({ force: true }).catch(() => null);
    const backendBuildIdTrimmed = normalizeBuildId(
      backendMeta?.backend?.buildId || backendMeta.backendBuildId,
    );
    const frontendBuildIdTrimmed = normalizeBuildId(frontend?.buildId);
    const policyDecision = evaluateFrontendUpdatePolicy({
      frontend,
      backendMeta,
      latestFrontendMeta,
    });
    let desiredPhase = policyDecision.phase;
    let desiredStatus = policyDecision.status;
    let reason = policyDecision.reason;
    let message = policyDecision.message;
    let updateKey = policyDecision.updateKey;
    const frontendPolicy = policyDecision.frontendPolicy;
    const latestFrontendBuildId = normalizeBuildId(frontendPolicy.latestBuildId);
    const latestFrontendVersion = normalizeVersion(frontendPolicy.latestVersion);
    const minFrontendBuildId = normalizeBuildId(frontendPolicy.minimumSupportedBuildId);
    const minFrontendVersion = normalizeVersion(frontendPolicy.minimumSupportedVersion);
    const diagnostics = policyDecision.diagnostics || {};

    if (!frontendPolicy.isAvailable) {
      this.debug("frontend_policy:unavailable", { backendBuildId: backendBuildIdTrimmed });
    }

    const suppression = this.loadSuppression();
    const suppressedUpdateKey = suppression?.updateKey || null;

    // Session-only suppression: if the user clicked "Restart later" for this frontend target, keep it hidden.
    if (desiredPhase === "UPDATE_AVAILABLE" && updateKey && suppressedUpdateKey === updateKey) {
      desiredPhase = "DISMISSED";
      desiredStatus = "up_to_date";
      reason = null;
      message = null;
    }

    // If the frontend update target changes, clear old suppression so future updates can appear normally.
    if (desiredPhase === "UPDATE_AVAILABLE" && updateKey && suppressedUpdateKey && suppressedUpdateKey !== updateKey) {
      this.clearSuppression();
    }

    // Release notes / semantic version detection:
    // - If update_available and semantic version changed -> attach "What's New" for latest release.
    // - If build-only deploy -> simple update banner only.
    let releaseNotes = this.releaseNotesInMemory;
    let whatsNew = null;
    if ((desiredStatus === "update_available" || desiredStatus === "force_update") && this.isAckDecisionReady(prev)) {
      releaseNotes = await this.ensureReleaseNotesLoaded().catch(() => this.releaseNotesInMemory);
      const latest = latestFrontendVersion || (Array.isArray(releaseNotes) && releaseNotes[0]?.version ? releaseNotes[0].version : null);
      const currentVersion = String(frontend?.version || "").trim();
      const updateSemanticVersion =
        latest && currentVersion && this.compareSemver(latest, currentVersion) > 0
          ? latest
          : null;

      if (updateSemanticVersion) {
        whatsNew = this.computeWhatsNew({
          frontendVersion: currentVersion,
          updateSemanticVersion,
          releaseNotes,
        });
        const dismissed = String(prev.dismissedWhatsNewVersion || "").trim();
        if (dismissed && whatsNew?.version && dismissed === whatsNew.version) {
          whatsNew = null;
        }
      }
    }

    const next = {
      ...prev,
      phase: desiredPhase,
      status: desiredStatus,
      reason,
      message,
      backend: backendMeta,
      frontendPolicy,
      updateKey: updateKey || null,
      suppressedUpdateKey: suppressedUpdateKey || null,
      releaseNotes: Array.isArray(releaseNotes) ? releaseNotes : prev.releaseNotes,
      releaseNotesLoadedAt:
        Array.isArray(releaseNotes) && releaseNotes.length > 0
          ? new Date().toISOString()
          : prev.releaseNotesLoadedAt,
      whatsNew: whatsNew || (desiredPhase === "IDLE" ? prev.whatsNew : null),
      detectedAt:
        desiredPhase !== "IDLE" && desiredPhase !== "DISMISSED"
          ? prev.detectedAt || new Date().toISOString()
          : null,
    };

    if (desiredPhase === "IDLE") {
      this.setRefreshPendingFlag(false);
      next.refreshPending = false;
      // If the backend build changed (or we are aligned), clear stale suppression automatically.
      if (suppressedUpdateKey) this.clearSuppression();
      next.suppressedUpdateKey = null;
    }

    if (commit) {
      this.debug("poll:state", {
        phase: next.phase,
        status: next.status,
        frontendBuildId: frontendBuildIdTrimmed,
        backendBuildId: backendBuildIdTrimmed,
        latestFrontendBuildId,
        latestFrontendVersion,
        minFrontendBuildId,
        minFrontendVersion,
        frontendForceUpdate: Boolean(frontendPolicy.forceUpdate),
        belowMinBuild: Boolean(diagnostics.belowMinBuild),
        belowMinVersion: Boolean(diagnostics.belowMinVersion),
        latestBuildAvailable: Boolean(diagnostics.latestBuildAvailable),
        latestVersionAvailable: Boolean(diagnostics.latestVersionAvailable),
        refreshPending: Boolean(next.refreshPending),
        suppressedUpdateKey: next.suppressedUpdateKey || "",
      });
      this.commitState(next, { broadcast: true });
    }
    return next;
  }

  reportChunkLoadError({ message } = {}) {
    const prev = this.state;
    const next = {
      ...prev,
      phase: prev.phase === "FORCE_UPDATE" ? prev.phase : "UPDATE_AVAILABLE",
      status: prev.status === "force_update" ? prev.status : "update_available",
      reason: "chunk_error",
      message: "New version available",
      detectedAt: prev.detectedAt || new Date().toISOString(),
    };
    this.commitState(next, { broadcast: true });

    if (isStreamingActive()) {
      this.setRefreshPending(true);
      return;
    }
    if (shouldThrottleHardReload()) return;
    this.requestRefresh();
  }
}

export const UpdateManager = new UpdateManagerImpl();
